import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import "./CreateNewStaff.css";

const API_BASE = "https://server.majestyfx.com";

export default function CreateNewStaff() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    roleType: "",
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin_fetch_roles.php`);
      if (response.data.success) {
        setRoles(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError("Failed to fetch roles");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation
    if (!form.username || !form.email || !form.password || !form.roleType) {
      setError("All fields are required");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/admin_roles_main.php`, {
        username: form.username,
        email: form.email,
        password: form.password,
        roleType: form.roleType,
      });

      if (response.data.success) {
        setSuccess("Staff created successfully! Redirecting...");
        setTimeout(() => {
          navigate("/all-staff");
        }, 2000);
      } else {
        setError(response.data.message || "Failed to create staff");
      }
    } catch (err) {
      setError("Error creating staff: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-staff-container">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <NavLink to="/">Home</NavLink> /{" "}
        <NavLink to="/all-staff">Manage Staff</NavLink> / Create New
      </div>

      {/* Header */}
      <div className="header-row">
        <h2 className="page-title">Create New Staff</h2>
        <NavLink to="/all-staff" className="btn-view-all">
          👁 View All
        </NavLink>
      </div>

      {/* Form Card */}
      <div className="form-card">
        {/* Success/Error Messages */}
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>
                Username <span className="required">*</span>
              </label>
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="Enter username"
                required
              />
            </div>

            <div className="form-group">
              <label>
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter email address"
                required
              />
            </div>

            <div className="form-group">
              <label>
                Password <span className="required">*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>
                Role <span className="required">*</span>
              </label>
              <select
                name="roleType"
                value={form.roleType}
                onChange={handleChange}
                required
              >
                <option value="">Select Role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <NavLink to="/all-staff" className="btn-cancel">
              Cancel
            </NavLink>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? "Creating..." : "Create Staff"}
            </button>
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="footer">
        <p>
          Copyright © 2025 <span className="brand">Ample Markets</span>. All
          rights reserved.
        </p>
        <p className="version">Version 2.0</p>
      </div>
    </div>
  );
}
